// ISO week arithmetic for the screens' period focus (`p=2026-W42`) and the in-memory API.
// Monday-based ISO 8601 weeks, computed in UTC so the result does not depend on the zone.
import { isoWeekKey } from "./focus";

const WEEK = /^(\d{4})-W(\d{2})$/;

/** Monday 00:00 UTC of an ISO week key. */
export function weekStart(key: string): Date {
  const m = WEEK.exec(key);
  if (!m) throw new Error(`not a week key: ${key}`);
  const year = Number(m[1]);
  const week = Number(m[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dow = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (dow - 1) + (week - 1) * 7);
  return monday;
}

/** The week key `delta` weeks after (negative: before) `key`. */
export function shiftWeek(key: string, delta: number): string {
  const d = weekStart(key);
  d.setUTCDate(d.getUTCDate() + 7 * delta + 3); // Thursday decides the ISO year
  return isoWeekKey(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** Whole weeks from `a` to `b` (positive when `b` is later). */
export function weeksBetween(a: string, b: string): number {
  return Math.round((weekStart(b).getTime() - weekStart(a).getTime()) / (7 * 86_400_000));
}

/** `YYYY-MM-DD` of a UTC date. */
export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The week of today, in the browser's calendar. */
export function currentWeek(today: Date = new Date()): string {
  return isoWeekKey(today);
}

/** "2026-W42" → "Oct 12 – Oct 18". */
export function weekRangeLabel(key: string): string {
  const start = weekStart(key);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const f = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  return `${f(start)} – ${f(end)}`;
}
